import { getCurrencySymbol, NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, Host, Inject, Input, OnInit, Optional } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatDivider } from '@angular/material/list';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { RootState } from '@next/core-lib/app';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import Utils from 'src/app/utils';

import { Discount } from '../../../../settings/models/settings.model';
import { getDiscount } from '../../../../settings/store/selectors/settings.selectors';
import { Calculation, PriceSetting } from '../../../models/feed-profit.model';
import { SETTINGS_DIALOG_HOST } from '../settings-dialog.token';

@Component({
  selector: 'app-company-details',
  templateUrl: './company-details.component.html',
  styleUrls: ['./company-details.component.scss'],
  imports: [
    ReactiveFormsModule,
    NgIf,
    MatDivider,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    NgFor,
    MatOption,
    AgrNumberDirective,
    MatSlideToggle,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class CompanyDetailsComponent implements OnInit {
  @Input() calculation: Calculation;

  location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);
  discount$: Observable<Discount> = this.store.select(getDiscount);

  priceSettingOptions: string[] = Object.values(PriceSetting);
  currencySymbol: string;

  form = new FormGroup({
    name: new FormControl<string>('', [Validators.required]),
    cause: new FormControl<string>(null, []),
    priceSetting: new FormControl<PriceSetting>(PriceSetting.BRUTO, []),
    numberOfSilos: new FormControl<number>(null, []),
    amountOfGrowFinisher: new FormControl<number>(null, []),
    showDiscounts: new FormControl<boolean>(false, []),
  });

  formSubscription: Subscription;

  initialLoad = true;

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    @Optional()
    @Host()
    @Inject(SETTINGS_DIALOG_HOST)
    public settingsComponent: { calculation: Calculation },
  ) {}

  ngOnInit() {
    this.currencySymbol = this.currencySymbol = getCurrencySymbol(
      this.localeService.getCustomerCurrency(),
      'narrow',
    );

    this.setCompanyDetails(this.calculation);
    this.initialLoad = false;
  }

  setCompanyDetails(calculation: Calculation) {
    this.controlledFormSetString(this.form.controls.name, calculation?.name);
    this.controlledFormSetString(this.form.controls.cause, calculation?.companyDetails?.cause);
    const newPriceSetting = calculation?.companyDetails?.priceSetting || PriceSetting.BRUTO;
    this.controlledFormSetPriceSetting(this.form.controls.priceSetting, newPriceSetting);
    this.controlledFormSetNumber(
      this.form.controls.numberOfSilos,
      calculation?.companyDetails?.numberOfSilos,
    );
    this.controlledFormSetNumber(
      this.form.controls.amountOfGrowFinisher,
      calculation?.companyDetails?.amountOfGrowFinisher,
    );
  }

  controlledFormSetNumber(formControl: FormControl<number>, value: number) {
    if (Utils.shouldUpdateNumericForm(formControl.value, value)) {
      formControl.setValue(value, { emitEvent: !this.initialLoad });
    }
  }

  controlledFormSetString(formControl: FormControl<string>, value: string) {
    if (Utils.shouldUpdateStringForm(formControl.value, value)) {
      formControl.setValue(value, { emitEvent: !this.initialLoad });
    }
  }

  controlledFormSetPriceSetting(formControl: FormControl<PriceSetting>, value: PriceSetting) {
    if ((formControl.value || value) && formControl.value !== value) {
      formControl.setValue(value, { emitEvent: !this.initialLoad });
    }
  }

  getCalculation() {
    const { name, cause, priceSetting, numberOfSilos, amountOfGrowFinisher } = this.form.value;
    return {
      ...this.settingsComponent.calculation,
      name,
      companyDetails: {
        cause,
        priceSetting,
        numberOfSilos,
        amountOfGrowFinisher,
      },
    };
  }
}
